import React, { useEffect, useState } from "react";
import { api, fmtEUR, fmtTL } from "../lib/api";
import { Trash2, Search, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AllPayments() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [party, setParty] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/all-payments");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (id) => {
    if (!window.confirm("Bu ödeme kaydını silmek istediğinize emin misiniz?")) return;
    await api.delete(`/payments/${id}`);
    toast.success("Silindi");
    load();
  };

  const cleanupOrphans = async () => {
    if (!window.confirm("Silinmiş müşteri veya tedarikçilere ait ödemeler kalıcı olarak silinsin mi?")) return;
    const r = await api.post("/admin/cleanup-orphans");
    toast.success(`${r.data.customer_deleted + r.data.supplier_deleted} kayıt temizlendi`);
    load();
  };

  const filtered = items.filter((p) => {
    if (party !== "all" && p.party !== party) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (p.case_name || "").toLowerCase().includes(s) ||
      (p.supplier_name || "").toLowerCase().includes(s) ||
      (p.notes || "").toLowerCase().includes(s) ||
      (p.bank_name || "").toLowerCase().includes(s)
    );
  });

  const totals = {
    cust_eur: filtered.filter(p => p.party === "customer").reduce((a, p) => a + (p.amount_eur || 0), 0),
    cust_tl: filtered.filter(p => p.party === "customer").reduce((a, p) => a + (p.amount_tl || 0), 0),
    supp_eur: filtered.filter(p => p.party === "supplier").reduce((a, p) => a + (p.amount_eur || 0), 0),
  };

  const orphanCount = items.filter(p =>
    (p.party === "customer" && p.case_name === "(silinmiş kayıt)") ||
    (p.party === "supplier" && p.supplier_name === "(silinmiş tedarikçi)")
  ).length;

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="all-payments-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">Tüm Ödemeler</div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl tracking-tight">Ödeme Hareketleri</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {orphanCount > 0 && (
            <button onClick={cleanupOrphans} className="h-10 px-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md hover:bg-red-100 flex items-center gap-2" data-testid="cleanup-orphans-btn">
              <AlertTriangle size={13} /> {orphanCount} kayıp bağı temizle
            </button>
          )}
          <button onClick={load} className="h-10 px-4 text-sm border border-zinc-200 rounded-md hover:bg-zinc-100 flex items-center gap-2" data-testid="reload-payments-btn">
            <RefreshCw size={13} /> Yenile
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Müşteri Tahsilat EUR" value={fmtEUR(totals.cust_eur)} />
        <MetricCard label="Müşteri Tahsilat TL" value={fmtTL(totals.cust_tl)} />
        <MetricCard label="Tedarikçiye Ödenen EUR" value={fmtEUR(totals.supp_eur)} />
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Müşteri, tedarikçi, banka, açıklama…" className="w-full h-10 pl-9 pr-3 border border-zinc-200 bg-white rounded-md text-sm focus:outline-none focus:border-zinc-950" data-testid="pay-search" />
        </div>
        <select value={party} onChange={(e) => setParty(e.target.value)} className="h-10 px-3 border border-zinc-200 rounded-md text-sm bg-white" data-testid="pay-party-filter">
          <option value="all">Tümü</option>
          <option value="customer">Müşteri Ödemeleri</option>
          <option value="supplier">Tedarikçi Ödemeleri</option>
        </select>
      </div>

      <div className="mt-4 grid-card overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-50 text-left">
            <tr className="border-b border-zinc-200">
              <Th>Tarih</Th><Th>Tür</Th><Th>Kime/Kimden</Th><Th>EUR</Th><Th>TL</Th><Th>Banka</Th><Th>Not</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const orphan = (p.party === "customer" && p.case_name === "(silinmiş kayıt)") || (p.party === "supplier" && p.supplier_name === "(silinmiş tedarikçi)");
              return (
                <tr key={p.id} className={`border-b border-zinc-100 ${orphan ? "bg-red-50/40" : "hover:bg-zinc-50"}`} data-testid={`pay-row-${p.id}`}>
                  <Td>{(p.date || "").slice(0, 10)}</Td>
                  <Td>{p.party === "customer" ? <span className="text-[#00913c]">Tahsilat</span> : <span className="text-[#b71c1c]">Tedarikçi</span>}</Td>
                  <Td className={orphan ? "italic text-red-700" : ""}>{p.party === "customer" ? p.case_name : p.supplier_name}</Td>
                  <Td className="tabular-nums">{fmtEUR(p.amount_eur)}</Td>
                  <Td className="tabular-nums">{fmtTL(p.amount_tl)}</Td>
                  <Td>{p.bank_name || "—"}</Td>
                  <Td className="text-xs text-zinc-500 max-w-xs truncate" title={p.notes}>{p.notes || "—"}</Td>
                  <Td><button onClick={() => del(p.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`del-pay-${p.id}`}><Trash2 size={13} /></button></Td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">Ödeme bulunamadı</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 label-overline font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const MetricCard = ({ label, value }) => (
  <div className="grid-card p-5">
    <div className="label-overline">{label}</div>
    <div className="mt-2 font-display font-black text-2xl">{value}</div>
  </div>
);
